import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Send, Loader2, SkipForward, Upload, User, MapPin, Shield, Zap, Eye, Camera, CheckCircle, Edit3 } from "lucide-react";
import { getPositionAttributes } from "../lib/positionAttributes";

const POSITIONS = ["Goalkeeper", "Centre-Back", "Left-Back", "Right-Back", "Defensive Midfielder", "Central Midfielder", "Attacking Midfielder", "Left Winger", "Right Winger", "Striker", "Centre-Forward"];
const FEET = ["Right", "Left", "Both"];
const PHYSICAL_RATINGS = ["Average", "Above Average", "Elite"];
const AVAILABILITY = ["Yes - Immediately", "Yes - Within 1 Month", "Yes - Within 3 Months", "Not Currently"];
const PLAY_STYLES = ["Direct", "Controlled", "Creative", "Physical", "Technical", "Mixed"];

const BASE_STEPS = [
  { id: "full_name", module: "identity", field: "full_name", q: "Let's start. What's your full name?", type: "text", required: true },
  { id: "age", module: "identity", field: "age", q: "How old are you?", type: "text" },
  { id: "nationality", module: "identity", field: "nationality", q: "What's your nationality?", type: "text" },
  { id: "height", module: "identity", field: "height", q: "Height in cm? (Skip if you prefer)", type: "text", skip: true },
  { id: "preferred_foot", module: "identity", field: "preferred_foot", q: "Which foot do you prefer?", type: "buttons", options: FEET },
  { id: "primary_position", module: "identity", field: "primary_position", q: "Primary position?", type: "buttons", options: POSITIONS },
  { id: "secondary_position", module: "identity", field: "secondary_position", q: "Any secondary position?", type: "buttons", options: ["None", ...POSITIONS] },
  { id: "current_club", module: "identity", field: "current_club", q: "Current club or academy? (Skip if unattached)", type: "text", skip: true },
  { id: "profile_image", module: "media", field: "profile_image", q: "Upload a profile photo. This is the first thing scouts see.", type: "upload", skip: true },
  { id: "player_type", module: "playing_identity", field: "player_type", q: "How would you describe your playing style?", type: "textarea" },
  // Attribute steps are injected here dynamically based on primary_position.
  { id: "__ATTRIBUTE_ANCHOR__" },
  { id: "areas_for_improvement", module: "playing_identity", field: "areas_for_improvement", q: "Any areas you're working to improve? (Skip if you prefer)", type: "textarea", skip: true },
  { id: "style_of_play", module: "in_possession", field: "style_of_play", q: "How would you categorise your style?", type: "buttons", options: PLAY_STYLES },
  { id: "receiving", module: "in_possession", field: "receiving", q: "How do you play in possession — passing, movement, receiving?", type: "textarea" },
  { id: "pressing_style", module: "out_of_possession", field: "pressing_style", q: "How do you contribute out of possession?", type: "textarea" },
  { id: "speed", module: "physical", field: "speed", q: "Speed?", type: "buttons", options: PHYSICAL_RATINGS },
  { id: "stamina", module: "physical", field: "stamina", q: "Stamina?", type: "buttons", options: PHYSICAL_RATINGS },
  { id: "strength_phys", module: "physical", field: "strength", q: "Physical strength?", type: "buttons", options: PHYSICAL_RATINGS },
  { id: "clubs_history", module: "experience", field: "clubs_history", q: "Walk me through your football journey — clubs, competitions, achievements.", type: "textarea" },
  { id: "highlight_video", module: "media", field: "highlight_video", q: "Got a highlight video link? (YouTube or Vimeo — skip if none)", type: "url", skip: true },
  { id: "contact_email", module: "contact", field: "contact_email", q: "Best email for scout enquiries?", type: "email" },
  { id: "availability", module: "contact", field: "availability", q: "Available for trials?", type: "buttons", options: AVAILABILITY },
];

function buildSteps(position) {
  const attrSteps = getPositionAttributes(position).map((a) => ({
    id: `attr_${a.id}`,
    module: "attributes",
    field: a.id,
    q: a.q,
    subQ: a.purpose,
    type: "attribute",
    category: a.category,
    label: a.label,
    options: a.options,
  }));
  const out = [];
  for (const step of BASE_STEPS) {
    if (step.id === "__ATTRIBUTE_ANCHOR__") {
      out.push(...attrSteps);
    } else {
      out.push(step);
    }
  }
  return out;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [inputVal, setInputVal] = useState("");
  const [moduleData, setModuleData] = useState({});
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ready, setReady] = useState(false);
  const [awaitingInput, setAwaitingInput] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [freeFormActive, setFreeFormActive] = useState(false);
  const moduleDataRef = useRef({});

  // Position-aware step list (rebuilds when primary_position changes)
  const primaryPosition = moduleData?.identity?.primary_position || "";
  const STEPS = useMemo(() => buildSteps(primaryPosition), [primaryPosition]);

  // Keep ref in sync with state so callbacks always have latest data
  useEffect(() => { moduleDataRef.current = moduleData; }, [moduleData]);

  useEffect(() => {
    const init = async () => {
      let loadedModuleData = {};
      let savedStep = 0;
      try {
        const { data } = await api.get("/onboarding/progress");
        if (data.modules && Object.keys(data.modules).length > 0) {
          loadedModuleData = data.modules;
          setModuleData(data.modules);
          moduleDataRef.current = data.modules;
        }
        if (data.current_step && data.current_step > 0) savedStep = data.current_step;
        if (data.is_onboarding_complete) { navigate("/contact"); return; }
      } catch {}

      const micro = localStorage.getItem("micro_onboarding");
      if (micro) {
        try {
          const { position, strengths, playerType } = JSON.parse(micro);
          const newModuleData = {};
          if (position) { newModuleData.identity = { primary_position: position }; await api.post("/onboarding/save", { module: "identity", data: { primary_position: position } }).catch(() => {}); }
          if (playerType || strengths) { const playData = {}; if (playerType) playData.player_type = playerType; if (strengths) playData.strengths = strengths.join(", "); newModuleData.playing_identity = playData; await api.post("/onboarding/save", { module: "playing_identity", data: playData }).catch(() => {}); }
          setModuleData((prev) => { const merged = { ...prev }; Object.entries(newModuleData).forEach(([mod, fields]) => { merged[mod] = { ...(merged[mod] || {}), ...fields }; }); loadedModuleData = merged; moduleDataRef.current = merged; return merged; });
          localStorage.removeItem("micro_onboarding");
        } catch {}
      }

      if (savedStep === 0 && Object.keys(loadedModuleData).length > 0) {
        for (let i = 0; i < STEPS.length; i++) {
          const s = STEPS[i];
          const val = loadedModuleData[s.module]?.[s.field];
          if (!val || val === "") { savedStep = i; break; }
          if (i === STEPS.length - 1) savedStep = STEPS.length;
        }
      }

      if (savedStep > 0) {
        const reconstructed = [];
        for (let i = 0; i < Math.min(savedStep, STEPS.length); i++) {
          const s = STEPS[i];
          const val = loadedModuleData[s.module]?.[s.field];
          reconstructed.push({ from: "system", text: s.q });
          if (val) { reconstructed.push({ from: "player", text: val }); reconstructed.push({ from: "system", text: "Noted." }); }
        }
        setMessages(reconstructed);
        setCurrentStep(Math.min(savedStep, STEPS.length));
      }
      setReady(true);
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (ready && messages.length === 0 && currentStep < STEPS.length) { addMsg("system", STEPS[0].q); setAwaitingInput(true); }
    else if (ready && messages.length > 0 && currentStep < STEPS.length) { addMsg("system", STEPS[currentStep].q); setAwaitingInput(true); }
  }, [ready]);

  useEffect(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [messages, evaluating, pendingSuggestion]);
  useEffect(() => { if (awaitingInput && inputRef.current) inputRef.current.focus(); }, [awaitingInput, currentStep]);

  const addMsg = (from, text) => setMessages((prev) => [...prev, { from, text }]);

  const getContext = useCallback(() => {
    const d = moduleDataRef.current;
    const id = d.identity || {};
    const pi = d.playing_identity || {};
    const parts = [];
    if (id.full_name) parts.push(`Name: ${id.full_name}`);
    if (id.primary_position) parts.push(`Position: ${id.primary_position}`);
    if (id.nationality) parts.push(`Nationality: ${id.nationality}`);
    if (id.age) parts.push(`Age: ${id.age}`);
    if (pi.player_type) parts.push(`Style: ${pi.player_type}`);
    return parts.join(". ");
  }, []);

  const saveAndAdvance = useCallback(async (value) => {
    const step = STEPS[currentStep];
    const prevData = moduleDataRef.current;
    const newData = { ...prevData, [step.module]: { ...(prevData[step.module] || {}), [step.field]: value || "" } };
    setModuleData(newData);
    moduleDataRef.current = newData;

    // Save to database — show error if fails
    try {
      await api.post("/onboarding/save", { module: step.module, data: newData[step.module] });
    } catch (err) {
      toast.error("Failed to save. Check your connection and try again.");
      setAwaitingInput(true);
      return;
    }

    addMsg("system", "Noted.");
    const nextStep = currentStep + 1;

    try {
      await api.post("/onboarding/save-step", { current_step: nextStep, is_complete: nextStep >= STEPS.length });
    } catch {}

    // Belief-building micro-line injected after specific steps (subtle, not salesy)
    const beliefFor = {
      primary_position: "This is what scouts actually look for.",
      player_type: "Most players get this part wrong.",
      attr_ball_progression: "If this is not clear, you get overlooked.",
      attr_pace_transitions: "If this is not clear, you get overlooked.",
      attr_finishing: "If this is not clear, you get overlooked.",
      attr_aerial_ability: "If this is not clear, you get overlooked.",
      attr_overlapping_runs: "If this is not clear, you get overlooked.",
      attr_screening: "If this is not clear, you get overlooked.",
      attr_creativity: "If this is not clear, you get overlooked.",
      attr_shot_stopping: "If this is not clear, you get overlooked.",
      attr_technique: "If this is not clear, you get overlooked.",
      highlight_video: "This is how players are assessed properly.",
    };
    const currentStepObj = STEPS[currentStep];
    const belief = currentStepObj && beliefFor[currentStepObj.id];
    if (belief) setTimeout(() => addMsg("system", belief), 150);

    if (nextStep >= STEPS.length) {
      setTimeout(() => {
        addMsg("system", "You're about to see your full profile.");
        setTimeout(() => addMsg("system", "This is how you'll be understood when someone reviews you."), 500);
        setTimeout(() => addMsg("system", "Most players never get to this level of clarity."), 1000);
        setTimeout(() => { addMsg("system", "Generating now…"); handleGenerate(); }, 1800);
      }, 400);
    } else {
      const next = STEPS[nextStep];
      const existingVal = newData[next.module]?.[next.field];
      if (existingVal && existingVal !== "") {
        setCurrentStep(nextStep);
        setTimeout(() => {
          addMsg("system", next.q);
          addMsg("player", existingVal);
          addMsg("system", "Already on file.");
          const nn = nextStep + 1;
          setCurrentStep(nn);
          if (STEPS[nn]) setTimeout(() => {
            addMsg("system", STEPS[nn].q);
            if (STEPS[nn].subQ) addMsg("system", STEPS[nn].subQ);
            setFreeFormActive(false);
            setAwaitingInput(true);
          }, 200);
        }, 200);
      } else {
        setCurrentStep(nextStep);
        setTimeout(() => {
          addMsg("system", next.q);
          if (next.subQ) addMsg("system", next.subQ);
          setFreeFormActive(false);
          setAwaitingInput(true);
        }, 300);
      }
    }
  }, [currentStep, STEPS]);

  const processAnswer = useCallback(async (value) => {
    const step = STEPS[currentStep];
    addMsg("player", value || "(skipped)");
    setAwaitingInput(false);
    setPendingSuggestion(null);

    // Format-only validation (hard gates)
    if (value && value.trim().length > 0) {
      if (step.field === "full_name") {
        if (!/[a-zA-ZÀ-ÿ]/.test(value) || /^\d+$/.test(value.trim()) || /^(.)\1{3,}$/.test(value.trim().replace(/\s/g, ""))) {
          addMsg("system", "That doesn't look like a real name. What's your full name?");
          setAwaitingInput(true);
          return;
        }
      }
      if (step.field === "age") {
        const num = parseInt(value.trim(), 10);
        if (isNaN(num) || num < 10 || num > 50 || value.trim() !== String(num)) {
          addMsg("system", "Enter your age as a number (10–50).");
          setAwaitingInput(true);
          return;
        }
      }
      if (step.field === "height" && value.trim().length > 0) {
        const num = parseInt(value.trim(), 10);
        if (isNaN(num) || num < 100 || num > 230 || value.trim() !== String(num)) {
          addMsg("system", "Enter height in cm (100–230), or skip.");
          setAwaitingInput(true);
          return;
        }
      }
      if (step.field === "contact_email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        addMsg("system", "That doesn't look like a valid email.");
        setAwaitingInput(true);
        return;
      }
      if (step.field === "highlight_video" && value.trim().length > 0 && !/^https?:\/\/.+/i.test(value.trim())) {
        addMsg("system", "Enter a URL starting with http:// or https://.");
        setAwaitingInput(true);
        return;
      }
    }

    // AI evaluation for text/textarea/attribute free-form — ASSISTIVE, not blocking
    const isAttrOptionPick = step.type === "attribute" && Array.isArray(step.options) && step.options.includes(value);
    const shouldAI = (step.type === "text" || step.type === "textarea" || step.type === "attribute") && value && value.trim().length > 0
      && !isAttrOptionPick
      && !["age", "height", "contact_email", "highlight_video"].includes(step.field);

    if (shouldAI) {
      setEvaluating(true);
      try {
        const { data: evalResult } = await api.post("/ai/evaluate-answer", {
          field_label: step.q, field_name: step.field, answer: value, context: getContext(),
        });
        setEvaluating(false);

        if (evalResult.quality === "rejected") {
          addMsg("system", evalResult.feedback || "That doesn't seem right for this question. Try again.");
          setAwaitingInput(true);
          return;
        }

        // UNCLEAR — guide the player: explanation + examples + purpose, let them pick one or rewrite
        if (evalResult.quality === "unclear") {
          if (evalResult.feedback) addMsg("system", evalResult.feedback);
          if (evalResult.purpose) addMsg("system", evalResult.purpose);
          const examples = Array.isArray(evalResult.examples) ? evalResult.examples.filter(Boolean).slice(0, 2) : [];
          setPendingSuggestion({
            kind: "unclear",
            original: value,
            examples,
          });
          return;
        }

        // For okay — show polished suggestion with Use this / Write my own buttons
        if (evalResult.quality === "okay" && evalResult.improved_version && evalResult.improved_version !== value) {
          const fb = evalResult.feedback || "Here's a cleaner version:";
          addMsg("system", fb);
          setPendingSuggestion({ kind: "polish", original: value, improved: evalResult.improved_version });
          return;
        }

        // Strong or no improvement needed — save and advance
        await saveAndAdvance(evalResult.improved_version || value);
        return;
      } catch {
        setEvaluating(false);
      }
    }

    // No AI needed or AI failed — just save and advance
    await saveAndAdvance(value);
  }, [currentStep, moduleData, getContext, saveAndAdvance, STEPS]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post("/ai/generate-profile");
      addMsg("system", "Profile ready.");
      setTimeout(() => navigate("/contact"), 1000);
    } catch (e) {
      addMsg("system", "Generation had an issue.");
      toast.error(e.response?.data?.detail || "Generation failed");
      setTimeout(() => navigate("/contact"), 1500);
    }
    setGenerating(false);
  };

  const handleSubmit = () => {
    if (evaluating || pendingSuggestion) return;
    if (!inputVal.trim() && !STEPS[currentStep]?.skip) return;
    processAnswer(inputVal.trim());
    setInputVal("");
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload?media_type=profile_image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      addMsg("player", "Photo uploaded");
      toast.success("Photo saved");
      const prevData = moduleDataRef.current;
      const newData = { ...prevData, media: { ...(prevData.media || {}), profile_image_path: data.path } };
      setModuleData(newData);
      moduleDataRef.current = newData;
      await saveAndAdvance(data.path);
    } catch (e) {
      toast.error("Upload failed. Try again or skip.");
      addMsg("system", "Upload failed. You can try again or skip this step.");
      setAwaitingInput(true);
    }
    setUploading(false);
  };

  if (!ready) {
    return (<div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  const step = STEPS[currentStep];
  const progressPct = Math.round((currentStep / STEPS.length) * 100);
  const id = moduleData.identity || {};
  const pi = moduleData.playing_identity || {};
  const phys = moduleData.physical || {};
  const exp = moduleData.experience || {};
  const media = moduleData.media || {};
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="bg-[#0A0A0A] flex flex-col" style={{ height: "calc(100vh - 64px)" }} data-testid="onboarding-page">
      {/* Progress bar */}
      <div className="border-b border-[#262626] px-4 py-2.5 flex items-center gap-4 flex-shrink-0">
        <div className="flex-1"><div className="progress-gold h-1.5"><div className="progress-gold-fill" style={{ width: `${progressPct}%` }} /></div></div>
        <span className="text-xs text-[#A3A3A3] font-bold tabular-nums">{progressPct}%</span>
      </div>

      {/* Split layout */}
      <div className="flex-1 flex min-h-0">
        {/* LEFT — Chat */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-container">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === "system" ? "justify-start" : "justify-end"} animate-fade-in`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-sm text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.from === "system" ? "bg-[#141414] border border-[#262626] text-[#E5E5E5]" : "bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37]"
                }`}>{msg.text}</div>
              </div>
            ))}
            {(generating || evaluating) && (
              <div className="flex justify-start animate-fade-in">
                <div className="bg-[#141414] border border-[#262626] px-4 py-3 rounded-sm flex items-center gap-2 text-sm text-[#A3A3A3]">
                  <Loader2 size={14} className="animate-spin" /> {generating ? "Generating profile..." : "Thinking..."}
                </div>
              </div>
            )}
            {/* Suggestion buttons */}
            {pendingSuggestion && pendingSuggestion.kind === "unclear" && (
              <div className="flex justify-start animate-fade-in">
                <div className="max-w-[85%] w-full bg-[#141414] border border-[#D4AF37]/30 px-4 py-3 rounded-sm space-y-3" data-testid="suggestion-card-unclear">
                  <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider">Examples</p>
                  <div className="space-y-2">
                    {pendingSuggestion.examples.map((ex, i) => (
                      <button
                        key={i}
                        onClick={async () => { const chosen = ex; setPendingSuggestion(null); addMsg("user", chosen); await saveAndAdvance(chosen); }}
                        className="w-full text-left bg-[#0E0E0E] border border-[#262626] hover:border-[#D4AF37]/60 px-3 py-2.5 rounded-sm text-sm text-[#E5E5E5] leading-snug italic transition-colors"
                        data-testid={`use-example-${i}`}
                      >
                        "{ex}"
                      </button>
                    ))}
                  </div>
                  <div className="pt-1 flex items-center justify-between gap-2 border-t border-[#262626]">
                    <p className="text-[10px] text-[#888]">Do you want to use one of these, or write your own?</p>
                    <button
                      onClick={() => { setPendingSuggestion(null); setAwaitingInput(true); }}
                      className="bg-[#1A1A1A] border border-[#262626] text-[#ccc] font-bold uppercase tracking-wider text-[10px] px-3 py-1.5 rounded-sm hover:text-[#F5F5F5] hover:border-[#D4AF37]/40 transition-colors flex-shrink-0"
                      data-testid="write-my-own-btn"
                    >
                      Write my own
                    </button>
                  </div>
                </div>
              </div>
            )}
            {pendingSuggestion && pendingSuggestion.kind === "polish" && (
              <div className="flex justify-start animate-fade-in">
                <div className="max-w-[85%] bg-[#141414] border border-[#D4AF37]/30 px-4 py-3 rounded-sm space-y-3" data-testid="suggestion-card">
                  <p className="text-sm text-[#E5E5E5] leading-relaxed italic">"{pendingSuggestion.improved}"</p>
                  <div className="flex gap-2">
                    <button onClick={async () => { const imp = pendingSuggestion.improved; setPendingSuggestion(null); addMsg("system", "Using improved version."); await saveAndAdvance(imp); }}
                      className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[10px] px-4 py-1.5 rounded-sm hover:bg-[#C5A059] transition-colors flex items-center gap-1" data-testid="use-this-btn">
                      <CheckCircle size={11} /> USE THIS
                    </button>
                    <button onClick={async () => { const orig = pendingSuggestion.original; setPendingSuggestion(null); addMsg("system", "Keeping your version."); await saveAndAdvance(orig); }}
                      className="bg-[#1A1A1A] border border-[#262626] text-[#999] font-bold uppercase tracking-wider text-[10px] px-4 py-1.5 rounded-sm hover:text-[#F5F5F5] transition-colors" data-testid="keep-mine-btn">
                      KEEP MINE
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fixed input */}
          {awaitingInput && step && !generating && !pendingSuggestion && (
            <div className="border-t border-[#262626] px-4 py-3 bg-[#0A0A0A] flex-shrink-0" data-testid="chat-input-area">
              {step.type === "buttons" ? (
                <div className="flex flex-wrap gap-2">
                  {step.options.map((opt) => (
                    <button key={opt} onClick={() => processAnswer(opt)} className="bg-[#1A1A1A] border border-[#262626] text-[#E5E5E5] text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-all" data-testid={`btn-${opt.replace(/\s/g, "-")}`}>{opt}</button>
                  ))}
                </div>
              ) : step.type === "attribute" && !freeFormActive ? (
                <div className="space-y-2" data-testid="attribute-step">
                  <div className="flex flex-col gap-1.5">
                    {step.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => processAnswer(opt)}
                        className="text-left bg-[#0E0E0E] border border-[#262626] text-[#E5E5E5] text-[13px] px-4 py-2.5 rounded-sm hover:border-[#A3813E]/60 hover:bg-[#141414] transition-all"
                        data-testid={`attr-opt-${opt.replace(/\s/g, "-")}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setFreeFormActive(true)}
                    className="w-full flex items-center justify-center gap-2 text-[11px] text-[#888] hover:text-[#D4AF37] py-2 transition-colors"
                    data-testid="attr-write-own"
                  >
                    <Edit3 size={11} /> Write my own answer
                  </button>
                </div>
              ) : step.type === "upload" ? (
                <label className="card-dark p-4 flex items-center justify-center gap-2 cursor-pointer border-dashed hover:border-[#D4AF37]/50 transition-colors" data-testid="upload-area">
                  {uploading ? <Loader2 size={16} className="animate-spin text-[#A3A3A3]" /> : <Camera size={16} className="text-[#A3A3A3]" />}
                  <span className="text-sm text-[#A3A3A3]">{uploading ? "Uploading..." : "Click to upload photo"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files?.[0])} disabled={uploading} />
                </label>
              ) : (
                <div className="flex gap-2">
                  {step.type === "textarea" || step.type === "attribute" ? (
                    <textarea ref={inputRef} value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                      className="input-dark flex-1 min-h-[44px] max-h-[100px] resize-none text-sm" placeholder="Type your answer..." data-testid="chat-textarea" disabled={evaluating} />
                  ) : (
                    <input ref={inputRef} type={step.type === "url" ? "url" : step.type === "email" ? "email" : "text"}
                      value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                      className="input-dark flex-1 text-sm py-2.5" placeholder="Type your answer..." data-testid="chat-input" disabled={evaluating} />
                  )}
                  <button onClick={handleSubmit} disabled={evaluating} className="bg-[#D4AF37] text-black px-4 rounded-sm hover:bg-[#C5A059] transition-colors disabled:opacity-50" data-testid="chat-send">
                    {evaluating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              )}
              {step.skip && (
                <button onClick={() => { processAnswer(""); setInputVal(""); }} className="text-xs text-[#A3A3A3] hover:text-[#F5F5F5] mt-2 flex items-center gap-1 transition-colors" data-testid="chat-skip">
                  <SkipForward size={12} /> Skip this
                </button>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — Live preview */}
        <div className="hidden lg:flex w-[380px] flex-col border-l border-[#262626] bg-[#0E0E0E]">
          <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2 flex-shrink-0">
            <Eye size={14} className="text-[#D4AF37]" />
            <span className="text-[10px] font-bold text-[#F5F5F5] uppercase tracking-wider">LIVE PROFILE PREVIEW</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Strength estimate */}
            {id.full_name && (() => {
              const has = (v) => Boolean(v && String(v).trim() && String(v).trim() !== "None");
              let pts = 0;
              if (has(id.full_name)) pts += 4; if (has(id.age)) pts += 2; if (has(id.nationality)) pts += 4;
              if (has(id.primary_position)) pts += 4; if (has(id.preferred_foot)) pts += 2;
              if (has(id.height)) pts += 2; if (has(id.current_club)) pts += 2;
              if (has(pi.player_type)) pts += 7; if (has(pi.strengths)) pts += 7;
              if (has(moduleData.in_possession?.receiving)) pts += 5;
              if (has(moduleData.out_of_possession?.pressing_style)) pts += 5;
              if (has(exp.clubs_history)) pts += 8;
              if (has(media.highlight_video)) pts += 25;
              const score = Math.min(100, pts);
              const r = 22; const circ = 2 * Math.PI * r; const off = circ - (score / 100) * circ;
              const color = score >= 75 ? "#D4AF37" : score >= 50 ? "#C5A059" : "#666";
              return (
                <div className="flex items-center gap-3 pb-3 border-b border-[#1A1A1A]" data-testid="onboarding-strength">
                  <div className="relative" style={{ width: 48, height: 48 }}>
                    <svg width={48} height={48} className="transform -rotate-90">
                      <circle cx={24} cy={24} r={r} fill="none" stroke="#1A1A1A" strokeWidth="3" />
                      <circle cx={24} cy={24} r={r} fill="none" stroke={color} strokeWidth="3" strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" className="transition-all duration-700" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center"><span className="text-xs font-black text-[#F5F5F5]">{score}</span></div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold text-[#888] uppercase tracking-wider">Estimated Strength</div>
                    <div className="text-[10px] text-[#ccc]">{score >= 50 ? "Building well" : "Keep going"}</div>
                  </div>
                </div>
              );
            })()}

            {/* Identity */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                {media.profile_image_path ? (
                  <img src={`${BACKEND_URL}/api/media/${media.profile_image_path}`} alt="" className="w-10 h-10 object-cover border border-[#262626] rounded-sm" />
                ) : (
                  <div className="w-10 h-10 bg-[#1A1A1A] border border-[#262626] rounded-sm flex items-center justify-center"><User size={16} className="text-[#555]" /></div>
                )}
                <div>
                  <div className="text-sm font-bold text-[#F5F5F5] uppercase tracking-tight">{id.full_name || "Player Name"}</div>
                  <div className="text-[10px] text-[#777]">{[id.primary_position, id.age && `${id.age} yrs`, id.nationality].filter(Boolean).join(" · ") || "Position · Age · Nationality"}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[{ l: "Position", v: id.primary_position }, { l: "Foot", v: id.preferred_foot }, { l: "Height", v: id.height ? `${id.height}cm` : null }, { l: "Club", v: id.current_club }].filter(f => f.v).map((f, i) => (
                  <div key={i} className="bg-[#141414] border border-[#1A1A1A] px-2.5 py-1.5 rounded-sm">
                    <div className="text-[8px] font-bold text-[#555] uppercase tracking-wider">{f.l}</div>
                    <div className="text-[11px] text-[#ccc]">{f.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {(pi.player_type || pi.strengths) && (
              <div className="border-t border-[#1A1A1A] pt-3">
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1"><Zap size={10} />PLAYING IDENTITY</div>
                {pi.player_type && <p className="text-[11px] text-[#aaa] leading-relaxed mb-1.5">{pi.player_type}</p>}
                {pi.strengths && <><div className="text-[8px] font-bold text-[#666] uppercase mb-1">STRENGTHS</div><p className="text-[11px] text-[#999] leading-relaxed">{pi.strengths}</p></>}
              </div>
            )}

            {(moduleData.in_possession?.receiving || moduleData.out_of_possession?.pressing_style) && (
              <div className="border-t border-[#1A1A1A] pt-3">
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1"><Shield size={10} />TACTICAL</div>
                {moduleData.in_possession?.receiving && <><div className="text-[8px] font-bold text-[#666] uppercase mb-1">IN POSSESSION</div><p className="text-[11px] text-[#999] leading-relaxed mb-1.5">{moduleData.in_possession.receiving}</p></>}
                {moduleData.out_of_possession?.pressing_style && <><div className="text-[8px] font-bold text-[#666] uppercase mb-1">OUT OF POSSESSION</div><p className="text-[11px] text-[#999] leading-relaxed">{moduleData.out_of_possession.pressing_style}</p></>}
              </div>
            )}

            {(phys.speed || phys.stamina || phys.strength) && (
              <div className="border-t border-[#1A1A1A] pt-3">
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">PHYSICAL</div>
                <div className="grid grid-cols-3 gap-1.5">
                  {[{ l: "Speed", v: phys.speed }, { l: "Stamina", v: phys.stamina }, { l: "Strength", v: phys.strength }].filter(f => f.v).map((f, i) => (
                    <div key={i} className="bg-[#141414] border border-[#1A1A1A] px-2 py-1.5 rounded-sm text-center">
                      <div className="text-[8px] font-bold text-[#555] uppercase">{f.l}</div>
                      <div className={`text-[10px] font-bold ${f.v === "Elite" ? "text-[#D4AF37]" : "text-[#999]"}`}>{f.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {exp.clubs_history && (
              <div className="border-t border-[#1A1A1A] pt-3">
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2">EXPERIENCE</div>
                <p className="text-[11px] text-[#999] leading-relaxed">{exp.clubs_history}</p>
              </div>
            )}

            {media.highlight_video && (
              <div className="border-t border-[#1A1A1A] pt-3">
                <div className="text-[9px] font-bold text-[#D4AF37] uppercase tracking-wider mb-1">VIDEO</div>
                <p className="text-[10px] text-[#888] break-all">{media.highlight_video}</p>
              </div>
            )}

            {!id.full_name && (
              <div className="text-center py-8"><div className="text-xs text-[#555]">Your profile will build here as you answer.</div></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
